<!-- Day Navigation for Multi-Day Events -->
@if (isMultiDay$ | async) {
  <app-day-navigation
    [dayItems]="dayItems"
    [currentDay]="currentSelectedDay"
    (daySelected)="onDaySelected($event)"
  />
}


<div class="pb-[80px] mx-2 sm:mx-0">
  @if (filteredSections$ | async; as sections) {
    @if (errorLoadingAgenda) {
      <div
        class="p-4 text-center text-red-600 bg-red-100 border border-red-400 rounded"
      >
        {{ errorLoadingAgenda }}
      </div>
    } @else {
      @if (sections.length > 0) {
        @for (section of sections; track $index) {
          <app-section-headline
            [title]="section.title"
            [iconImg]="section.iconComponent"
            [iconColor]="section.iconColor"
          />
          <div class="px-3 sm:px-4">
            @for (item of section.items; track $index) {
              <app-shared-card
                additionalClasses="mb-3 md:mb-4 p-0 relative overflow-hidden transition-all duration-300"
                [class.shadow-lg]="getItemCurrentStatusObservable(item) | async">
                <!-- Timeline indicator on the left edge -->
                <div class="absolute left-0 top-0 bottom-0 transition-all duration-300"
                     [class.w-3]="!(getItemCurrentStatusObservable(item) | async)"
                     [class.sm:w-[18px]]="!(getItemCurrentStatusObservable(item) | async)"
                     [class.w-4]="getItemCurrentStatusObservable(item) | async"
                     [class.sm:w-[24px]]="getItemCurrentStatusObservable(item) | async"
                     [class.shadow-md]="getItemCurrentStatusObservable(item) | async"
                     [style.background-color]="getItemTimelineColorObservable(item, section.dayTime) | async">
                </div>

                <div class="p-3 md:p-4 transition-all duration-300"
                     [class.pl-5]="!(getItemCurrentStatusObservable(item) | async)"
                     [class.md:pl-6]="!(getItemCurrentStatusObservable(item) | async)"
                     [class.pl-7]="getItemCurrentStatusObservable(item) | async"
                     [class.md:pl-8]="getItemCurrentStatusObservable(item) | async"
                     [style.background-color]="getItemBackgroundColorObservable(item, section.dayTime) | async">
                  <div class="flex items-start justify-between w-full">
                    <div class="flex items-start gap-3 sm:gap-4">
                      <div class="flex flex-col items-center justify-center min-w-[60px] sm:min-w-[70px] text-center">
                        <div class="text-xl sm:text-2xl font-bold tracking-tight transition-colors duration-300"
                             [class.text-secondary-text]="getItemPastStatusObservable(item) | async">
                          {{ item.startTime | date: "H:mm" }}
                        </div>
                        <div class="text-base sm:text-lg font-bold tracking-tight text-secondary-text mt-0.5 transition-colors duration-300">
                          {{ item.endTime | date: "H:mm" }}
                        </div>
                        <div class="text-xl sm:text-2xl font-bold tracking-tight transition-colors duration-300"
                             [class.text-secondary-text]="getItemPastStatusObservable(item) | async">
                          <app-live-badge [isLive]="getItemCurrentStatusObservable(item) | async" />
                        </div>
                      </div>
                      <div class="w-px bg-gray-200 self-stretch flex-shrink-0"></div>
                      <div class="flex-1">
                        <div class="flex items-center gap-2 mb-1">
                          <div class="font-semibold text-base sm:text-lg transition-colors duration-300"
                               [class.text-secondary-text]="getItemPastStatusObservable(item) | async">
                            {{ item.title }}
                          </div>
                        </div>
                        <div
                          class="text-sm text-secondary-text mt-1 transition-colors duration-300 flex items-center gap-1"
                          [class.cursor-pointer]="!!item.location"
                          (click)="!!item.location && showLocation(item.location)"
                        >
                          @if (item.isMandatory) {
                            <app-badge label="Alle" tailwindColor="red" />
                          } @else {
                            <app-badge label="Individuell" tailwindColor="green" />
                          }
                          @if (item.location?.name) {
                            <lucide-icon [img]="MapPin" class="w-3 h-3 sm:w-4 sm:h-4" />
                            {{ item.location?.name }}
                          }
                        </div>
                        @if (item.description) {
                          <div class="mt-1 sm:mt-2">
                            @if (needsDescriptionExpansion(item)) {
                              <button
                                (click)="toggleDescription(item.id)"
                                class="w-full text-left cursor-pointer group flex items-start gap-2 overflow-hidden"
                                type="button"
                                [attr.aria-expanded]="isDescriptionExpanded(item.id)"
                              >
                                <div
                                  class="text-sm text-secondary-text transition-colors duration-300 min-w-0 flex-1"
                                  [class.line-clamp-1]="!isDescriptionExpanded(item.id)"
                                  [class.break-words]="isDescriptionExpanded(item.id)"
                                  [innerHTML]="item.description | enforceNewLine"
                                >
                                </div>
                                <div class="flex items-center justify-center text-sm text-secondary-text mt-0.5 flex-shrink-0">
                                  <lucide-icon
                                    [img]="isDescriptionExpanded(item.id) ? ChevronUp : ChevronDown"
                                    size="16"
                                    class="transition-transform duration-200"
                                  />
                                </div>
                              </button>
                            } @else {
                              <div class="text-sm text-secondary-text break-words transition-colors duration-300" [innerHTML]="item.description | enforceNewLine"></div>
                            }
                          </div>
                        }
                        @if (item.speakers && item.speakers.length > 0) {
                          <div class="text-sm text-secondary-text mt-1 sm:mt-2 transition-colors duration-300">
                            <span class="font-medium">Sprecher:</span>
                            @for (speaker of item.speakers; track speaker.id) {
                              <span class="ml-1">{{ speaker.firstName }} {{ speaker.lastName }}</span>
                              @if (!$last) {
                                <span>,</span>
                              }
                            }
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </app-shared-card>
            }
          </div>
        }
      } @else {
        <div class="p-4 text-center text-gray-500">
          Keine Termine vorhanden.
        </div>
      }
    }
  } @else {
    @if (errorLoadingAgenda) {
      <div
        class="p-4 text-center text-red-600 bg-red-100 border border-red-400 rounded"
      >
        {{ errorLoadingAgenda }}
      </div>
    } @else {
      <div class="p-4 text-center text-gray-500">Lade Termine...</div>
    }
  }
</div>
